import { useState, useEffect, useMemo } from 'react'
import { FiDownload, FiFileText, FiCalendar, FiMapPin, FiLayers, FiUser, FiX } from 'react-icons/fi'
import * as XLSX from 'xlsx'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import { useEmpresaGestion } from '../../contexts/EmpresaGestionContext'
import { PageHeader, Stat, Card, Btn, Empty, Pill, Avatar } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * ReportesFichajes (V2) — versión rediseñada con DS v2.
 *
 * Misma lógica funcional que el original:
 *   - Filtros mes/año/centro/grupo/empleado
 *   - 5 KPIs calculados client-side
 *   - Export Excel (SheetJS) y PDF (backend)
 *   - Tabla previsualización
 *
 * Cambios respecto al original:
 *   - PageHeader con eyebrow numerada y acciones de exportación arriba
 *   - Filtros en una FilterBar compacta (mismo bg-bg2 + border-line)
 *   - StatCards en lugar de tarjetas con gradient
 *   - Tabla con avatares, números tabulares y Pill warn para "Sin fichar"
 *   - Empty state usando primitive Empty
 */

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

const fmtH = (m) => { if (!m) return '0:00'; return `${Math.floor(m/60)}:${String(m%60).padStart(2,'0')}` }
const fmtDur   = (m) => { if (!m) return '—'; return `${Math.floor(m/60)}h ${m%60}m` }

// Select estilizado en pill v2 — uso interno
function FilterSelect({ icon: Icon, label, value, onChange, children }) {
  return (
    <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] text-[12.5px]">
      {Icon && <Icon size={13} className="text-[var(--ink-3)] flex-shrink-0" />}
      <label className="text-[var(--ink-3)] whitespace-nowrap">{label}</label>
      <select
        value={value}
        onChange={onChange}
        className="bg-transparent outline-none text-[var(--ink)] font-medium cursor-pointer"
      >
        {children}
      </select>
    </div>
  )
}

export default function ReportesFichajesV2() {
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()

  // Datos maestros
  const [empleados,  setEmpleados]  = useState([])
  const [grupos,     setGrupos]     = useState([])
  const [centros,    setCentros]    = useState([])

  // Filtros
  const [filtroGrupo,     setFiltroGrupo]     = useState('')
  const [filtroCentro,    setFiltroCentro]    = useState('')
  const [filtroEmpleado,  setFiltroEmpleado]  = useState('todos')
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1)
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear())

  // Datos
  const [fichajes,  setFichajes]  = useState([])
  const [resumen,   setResumen]   = useState(null)
  const [loading,   setLoading]   = useState(false)
  const [generando, setGenerando] = useState(false)

  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaGestionId) {
      const sep = endpoint.includes('?') ? '&' : '?'
      return `${API_URL}${endpoint}${sep}empresa_id=${empresaGestionId}`
    }
    return `${API_URL}${endpoint}`
  }

  // Cargar maestros al montar
  useEffect(() => {
    const token = localStorage.getItem('token')
    const h = { 'Authorization': `Bearer ${token}` }
    Promise.all([
      fetch(buildApiUrl('/empleados'), { headers: h }).then(r => r.ok ? r.json() : { empleados: [] }),
      fetch(buildApiUrl('/grupos-empleados'), { headers: h }).then(r => r.ok ? r.json() : []),
      fetch(buildApiUrl('/centros-trabajo'), { headers: h }).then(r => r.ok ? r.json() : []),
    ]).then(([empData, grpData, ctData]) => {
      setEmpleados(empData.empleados || [])
      setGrupos(Array.isArray(grpData) ? grpData : grpData.grupos || [])
      setCentros(Array.isArray(ctData) ? ctData : ctData.centros || [])
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaGestionId])

  // Empleados filtrados según grupo/centro
  const empleadosFiltrados = useMemo(() => {
    let lista = empleados
    if (filtroGrupo) {
      lista = lista.filter(e => String(e.grupo_id) === filtroGrupo)
    }
    if (filtroCentro) {
      const gruposDeCentro = grupos
        .filter(g => String(g.centro_id || g.centro_trabajo_id) === filtroCentro)
        .map(g => String(g.id))
      lista = lista.filter(e => gruposDeCentro.includes(String(e.grupo_id)))
    }
    return lista
  }, [empleados, grupos, filtroGrupo, filtroCentro])

  // Si el empleado seleccionado ya no está en la lista filtrada → reset
  useEffect(() => {
    if (filtroEmpleado !== 'todos') {
      const sigueDisponible = empleadosFiltrados.some(e => String(e.id) === filtroEmpleado)
      if (!sigueDisponible) setFiltroEmpleado('todos')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empleadosFiltrados])

  // Cargar fichajes al cambiar filtros
  useEffect(() => {
    cargarDatos()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEmpleado, mesSeleccionado, anioSeleccionado, filtroGrupo, filtroCentro])

  const cargarDatos = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const endpoint = filtroEmpleado !== 'todos'
        ? `/fichajes/todos?empleado_id=${filtroEmpleado}&mes=${mesSeleccionado}&anio=${anioSeleccionado}`
        : `/fichajes/todos?mes=${mesSeleccionado}&anio=${anioSeleccionado}`

      const res = await fetch(buildApiUrl(endpoint), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        let data = await res.json()

        if (filtroEmpleado === 'todos' && empleadosFiltrados.length < empleados.length) {
          const ids = new Set(empleadosFiltrados.map(e => e.id))
          data = data.filter(f => ids.has(f.empleado_id))
        }

        setFichajes(data)
        calcularResumen(data)
      }
    } catch {
      toast.error('Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }

  const calcularResumen = (data) => {
    const completados = data.filter(f => f.hora_salida && f.tiempo_total)
    const dias = new Set(completados.map(f => `${f.empleado_id}_${f.fecha}`)).size
    const totalMin = completados.reduce((s, f) => s + (f.tiempo_total || 0), 0)
    const pausasMin = completados.reduce((s, f) => s + (f.tiempo_pausas || 0), 0)
    const efectivoMin = totalMin - pausasMin
    setResumen({
      dias,
      total:    fmtH(totalMin),
      pausas:   fmtH(pausasMin),
      efectivo: fmtH(efectivoMin),
      sin_salida: data.filter(f => !f.hora_salida).length
    })
  }

  const exportarExcel = () => {
    try {
      const wb = XLSX.utils.book_new()
      const nombreMes = MESES[mesSeleccionado - 1]

      // Detalle
      const filas = fichajes.map(f => ({
        'Empleado':        f.empleado_nombre || '-',
        'DNI/NIF':         f.empleado_dni || '-',
        'Fecha':           f.fecha,
        'Día semana':      new Date(f.fecha + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long' }),
        'Hora entrada':    f.hora_entrada || '-',
        'Hora salida':     f.hora_salida || 'Sin fichar',
        'Total (h:m)':     fmtH(f.tiempo_total),
        'Pausas (h:m)':    fmtH(f.tiempo_pausas),
        'Efectivo (h:m)':  fmtH(f.tiempo_efectivo || (f.tiempo_total - (f.tiempo_pausas||0))),
        'Estado':          f.hora_salida ? 'Completo' : 'Incompleto',
      }))
      const ws1 = XLSX.utils.json_to_sheet(filas)
      ws1['!cols'] = [
        { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 14 }, { wch: 11 }
      ]
      XLSX.utils.book_append_sheet(wb, ws1, 'Detalle fichajes')

      // Resumen por empleado
      const porEmpleado = {}
      fichajes.forEach(f => {
        if (!porEmpleado[f.empleado_nombre]) {
          porEmpleado[f.empleado_nombre] = { nombre: f.empleado_nombre, dias: new Set(), totalMin: 0, pausasMin: 0, sinSalida: 0 }
        }
        const e = porEmpleado[f.empleado_nombre]
        if (f.hora_salida) e.dias.add(f.fecha)
        e.totalMin += f.tiempo_total || 0
        e.pausasMin += f.tiempo_pausas || 0
        if (!f.hora_salida) e.sinSalida++
      })
      const resumenRows = Object.values(porEmpleado).map(e => ({
        'Empleado':            e.nombre,
        'Días trabajados':     e.dias.size,
        'Horas totales':       fmtH(e.totalMin),
        'Horas en pausas':     fmtH(e.pausasMin),
        'Horas efectivas':     fmtH(e.totalMin - e.pausasMin),
        'Fichajes incompletos': e.sinSalida,
      }))
      const ws2 = XLSX.utils.json_to_sheet(resumenRows)
      ws2['!cols'] = [{ wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 20 }]
      XLSX.utils.book_append_sheet(wb, ws2, 'Resumen por empleado')

      // Portada
      const meta = [
        ['Reporte de Registro Horario'],
        [],
        ['Período:', `${nombreMes} ${anioSeleccionado}`],
        ['Generado:', new Date().toLocaleString('es-ES')],
        ['Empleados incluidos:', filtroEmpleado === 'todos' ? 'Todos' : (empleados.find(e => String(e.id) === filtroEmpleado)?.nombre || filtroEmpleado)],
        filtroGrupo ? ['Grupo:', grupos.find(g => String(g.id) === filtroGrupo)?.nombre || filtroGrupo] : null,
        filtroCentro ? ['Centro:', centros.find(c => String(c.id) === filtroCentro)?.nombre || filtroCentro] : null,
        [],
        ['Total fichajes:', fichajes.length],
        ['Días trabajados:', resumen?.dias || 0],
        ['Horas totales:', resumen?.total || '0:00'],
        ['Horas efectivas:', resumen?.efectivo || '0:00'],
        ['Fichajes sin salida:', resumen?.sin_salida || 0],
        [],
        ['Normativa:', 'Conforme al RD-Ley 8/2019 sobre registro de jornada laboral'],
        ['Conservación:', 'Este documento debe conservarse durante 4 años'],
      ].filter(Boolean)
      const ws3 = XLSX.utils.aoa_to_sheet(meta)
      ws3['!cols'] = [{ wch: 25 }, { wch: 35 }]
      XLSX.utils.book_append_sheet(wb, ws3, 'Información')
      wb.SheetNames = ['Información', 'Detalle fichajes', 'Resumen por empleado']

      const nombreArchivo = `Fichajes_${nombreMes}_${anioSeleccionado}${filtroEmpleado !== 'todos' ? '_' + (empleados.find(e => String(e.id) === filtroEmpleado)?.nombre?.replace(/\s+/g,'_') || '') : ''}.xlsx`
      XLSX.writeFile(wb, nombreArchivo)
      toast.success('Excel generado correctamente')
    } catch (err) {
      console.error(err)
      toast.error('Error al generar Excel')
    }
  }

  const exportarPDF = async () => {
    setGenerando(true)
    try {
      const token = localStorage.getItem('token')
      const empId = filtroEmpleado === 'todos' ? 'todos' : filtroEmpleado
      const url = `${API_URL}/fichajes/exportar-pdf?empleado_id=${empId}&mes=${mesSeleccionado}&anio=${anioSeleccionado}`
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })

      if (res.ok) {
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = window.URL.createObjectURL(blob)
        a.download = `Fichajes_${MESES[mesSeleccionado-1]}_${anioSeleccionado}${empId !== 'todos' ? '_' + (empleados.find(e => String(e.id) === empId)?.nombre?.replace(/\s+/g,'_') || '') : ''}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(a.href)
        toast.success('PDF generado correctamente')
      } else {
        const err = await res.json()
        toast.error(err.error || 'Error al generar PDF')
      }
    } catch {
      toast.error('Error al generar PDF')
    } finally {
      setGenerando(false)
    }
  }

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)
  const hayFiltrosActivos = filtroGrupo || filtroCentro
  const tieneDatos = fichajes.length > 0

  return (
    <>
      <PageHeader
        num="§ R1"
        kicker="Informes"
        title="Reportes de fichajes."
        italic="Listos para inspección."
        sub={`${MESES[mesSeleccionado - 1]} ${anioSeleccionado} · ${fichajes.length} ${fichajes.length === 1 ? 'registro' : 'registros'}`}
        actions={
          <>
            <Btn
              variant="ghost"
              size="sm"
              onClick={exportarPDF}
              disabled={generando || !tieneDatos}
            >
              <FiFileText size={13} />
              {generando ? 'Generando…' : 'PDF'}
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              onClick={exportarExcel}
              disabled={!tieneDatos}
            >
              <FiDownload size={13} />
              Excel
            </Btn>
          </>
        }
      />

      <div className="px-7 py-6 space-y-6">
        {/* ── FilterBar ── */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            icon={FiCalendar}
            label="Mes"
            value={mesSeleccionado}
            onChange={(e) => setMesSeleccionado(parseInt(e.target.value))}
          >
            {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </FilterSelect>

          <FilterSelect
            label="Año"
            value={anioSeleccionado}
            onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
          >
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </FilterSelect>

          {centros.length > 0 && (
            <FilterSelect
              icon={FiMapPin}
              label="Centro"
              value={filtroCentro}
              onChange={(e) => { setFiltroCentro(e.target.value); setFiltroGrupo(''); setFiltroEmpleado('todos') }}
            >
              <option value="">Todos</option>
              {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </FilterSelect>
          )}

          {grupos.length > 0 && (
            <FilterSelect
              icon={FiLayers}
              label="Grupo"
              value={filtroGrupo}
              onChange={(e) => { setFiltroGrupo(e.target.value); setFiltroEmpleado('todos') }}
            >
              <option value="">Todos</option>
              {grupos
                .filter(g => !filtroCentro || String(g.centro_id || g.centro_trabajo_id) === filtroCentro)
                .map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </FilterSelect>
          )}

          <FilterSelect
            icon={FiUser}
            label="Empleado"
            value={filtroEmpleado}
            onChange={(e) => setFiltroEmpleado(e.target.value)}
          >
            <option value="todos">
              {empleadosFiltrados.length < empleados.length
                ? `Todos (${empleadosFiltrados.length})`
                : 'Todos'}
            </option>
            {empleadosFiltrados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </FilterSelect>

          {hayFiltrosActivos && (
            <button
              type="button"
              onClick={() => { setFiltroCentro(''); setFiltroGrupo(''); setFiltroEmpleado('todos') }}
              className="inline-flex items-center gap-1 h-9 px-3 text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <FiX size={12} /> Quitar filtros
            </button>
          )}
        </div>

        {/* ── KPIs ── */}
        {resumen && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Stat
              label="Días trabajados"
              value={resumen.dias}
              sub={`${MESES[mesSeleccionado - 1]} ${anioSeleccionado}`}
            />
            <Stat
              label="Horas totales"
              value={resumen.total}
              unit="h"
              sub="entrada → salida"
            />
            <Stat
              label="Horas en pausas"
              value={resumen.pausas}
              unit="h"
              sub="comida y descansos"
            />
            <Stat
              label="Horas efectivas"
              value={resumen.efectivo}
              unit="h"
              sub="totales − pausas"
            />
            <Stat
              label="Sin hora de salida"
              value={resumen.sin_salida}
              sub={resumen.sin_salida > 0 ? 'Requieren revisión' : 'Todo registrado'}
              delta={resumen.sin_salida > 0 ? `${resumen.sin_salida} fichaje${resumen.sin_salida === 1 ? '' : 's'}` : null}
              deltaTone={resumen.sin_salida > 0 ? 'red' : 'emerald'}
            />
          </div>
        )}

        {/* ── Tabla ── */}
        {loading ? (
          <Card eyebrow="Vista previa" title="Cargando…">
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-9 rounded-soft bg-[var(--bg-2)] animate-pulse" />
              ))}
            </div>
          </Card>
        ) : tieneDatos ? (
          <Card
            eyebrow="Vista previa"
            title={`Detalle de fichajes — ${MESES[mesSeleccionado - 1]} ${anioSeleccionado}`}
            action={<span className="text-[11.5px] text-[var(--ink-3)] tnum">{fichajes.length} registros</span>}
            bodyClassName="p-0"
          >
            <div className="overflow-x-auto">
              <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] bg-[var(--bg-2)]/50 border-b border-[var(--line)] min-w-[720px]">
                <div className="col-span-3">Empleado</div>
                <div className="col-span-2">Fecha</div>
                <div className="col-span-1">Entrada</div>
                <div className="col-span-2">Salida</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1 text-right">Pausas</div>
                <div className="col-span-2 text-right">Efectivo</div>
              </div>
              <div className="divide-y divide-[var(--line)] min-w-[720px]">
                {fichajes.map(f => {
                  const sinSalida = !f.hora_salida
                  return (
                    <div
                      key={f.id}
                      className={`grid grid-cols-12 items-center px-5 py-3 text-[12.5px] hover:bg-[var(--bg-2)]/40 transition-colors ${sinSalida ? 'bg-[rgba(239,68,68,.04)]' : ''}`}
                    >
                      <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                        <Avatar name={f.empleado_nombre || '?'} size={26} />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{f.empleado_nombre || '—'}</div>
                          {f.empleado_dni && (
                            <div className="text-[10.5px] text-[var(--ink-3)] font-mono">{f.empleado_dni}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2 font-mono tnum text-[var(--ink-2)]">{formatearFecha(f.fecha)}</div>
                      <div className="col-span-1 font-mono tnum">{f.hora_entrada || '—'}</div>
                      <div className="col-span-2">
                        {sinSalida
                          ? <Pill tone="red">Sin fichar</Pill>
                          : <span className="font-mono tnum">{f.hora_salida}</span>
                        }
                      </div>
                      <div className="col-span-1 text-right font-mono tnum">{fmtDur(f.tiempo_total)}</div>
                      <div className="col-span-1 text-right font-mono tnum text-[var(--ink-3)]">{fmtDur(f.tiempo_pausas)}</div>
                      <div className="col-span-2 text-right font-mono tnum font-medium">
                        {fmtDur(f.tiempo_efectivo || (f.tiempo_total ? f.tiempo_total - (f.tiempo_pausas || 0) : null))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <Empty
              title="Nada que mostrar."
              sub="Cambia el período o quita filtros para ver los fichajes registrados."
              action={hayFiltrosActivos ? (
                <Btn
                  variant="ghost"
                  size="sm"
                  onClick={() => { setFiltroCentro(''); setFiltroGrupo(''); setFiltroEmpleado('todos') }}
                >
                  <FiX size={13} /> Quitar filtros
                </Btn>
              ) : null}
            />
          </Card>
        )}
      </div>
    </>
  )
}
